/**
 * Canonical renovation_v1 bot_settings defaults (renovation_bot_settings_defaults.json).
 * id / user_id / email are set by the backend on save.
 * Regenerate RENOVATION_BOT_SETTINGS_DEFAULTS via: python backend/scripts/export_renovation_defaults_to_js.py
 */

export const CANONICAL_APPOINTMENT_TYPE_KEYS = [
  "free_quote_inspection",
  "phone_video_consult"
];

export const RENOVATION_VISIT_APPOINTMENT_TYPE = "free_quote_inspection";
export const RENOVATION_CALL_APPOINTMENT_TYPE = "phone_video_consult";

export const CANONICAL_HANDOFF_ROLE_TYPES = [
  "general",
  "emergency",
  "warranty",
  "commercial"
];

export const LEGACY_APPOINTMENT_TYPE_KEYS = [
  "free_measure_and_quote",
  "design_consultation",
  "kitchen_renovation_quote",
  "bathroom_renovation_quote",
  "emergency_make_safe",
  "emergency_leak_callout",
  "restoration_consult",
  "repair_booking",
  "warranty_callback",
  "annual_maintenance_booking",
  "insurance_assessment",
  "commercial_site_walk"
];

export const CANONICAL_DISQUAL_ENUMS = {
  unqualified_action: ["decline_politely", "refer_to_specialist", "manager", "website"],
  tenant_policy: ["require_owner_consent", "accept", "decline"],
  asbestos_policy: [
    "refer_to_licensed_asbestos_specialist",
    "accept",
    "refer_to_partner",
    "decline",
  ],
};

export const CANONICAL_TONE_STYLES = [
  "professional_friendly",
  "blue_collar_professional",
  "friendly_energetic",
  "professional_corporate",
];

export const LEGACY_TONE_STYLE_MAP = {
  blue_collar_professional: "professional_friendly",
  friendly_energetic: "professional_friendly",
  professional_corporate: "professional_friendly",
};

export const HANDOFF_TRIGGER_LEGACY_MAP = {
  human_requested_3_times: "customer_requests_human",
  customer_complaint_or_angry: "aggressive_behavior",
};

export const TENANT_POLICY_LEGACY_MAP = {
  request_owner_details: "require_owner_consent",
  accept: "accept",
  decline: "decline",
};

export const ASBESTOS_POLICY_LEGACY_MAP = {
  refer_to_partner: "refer_to_licensed_asbestos_specialist",
  accept: "accept",
  decline: "decline",
};

export const UNQUALIFIED_ACTION_LEGACY_MAP = {
  manager: "decline_politely",
  decline: "decline_politely",
  website: "website",
};

/** @type { bot_type: string, knowledge_base: object, qualification_logic: object, scheduling: object, tone_of_voice: object } */
export const RENOVATION_BOT_SETTINGS_DEFAULTS = {
  "bot_type": "renovation_v1",
  "knowledge_base": {
    "company_name": "Coastal Renovation Group",
    "trading_name": "Coastal Renovation Group",
    "founded_year": 2014,
    "website_url": "https://coastalrenovationgroup.com.au",
    "company_phone": "+61 2 4222 0001",
    "company_email": "hello@coastalrenovationgroup.com.au",
    "founders": [
      "Jovan Dedovic",
    ],
    "social_networks": [
      {
        "network": "instagram",
        "url": "https://instagram.com/coastalrenovationgroup",
      },
      {
        "network": "facebook",
        "url": "https://facebook.com/coastalrenovationgroup",
      },
    ],
    "office_location": {
      "address": "200 Church St, Wollongong NSW 2500, Australia",
      "is_customer_facing": true,
      "country": "AU",
      "state": "NSW",
      "geo": {
        "lat": -34.4242643,
        "lng": 150.8932862,
      },
    },
    "service_area": {
      "zip_codes": [
        "any",
      ],
      "max_drive_radius_km": 80,
      "crosses_state_borders": false,
      "rejection_message": "We currently service the Illawarra, South Coast, and Southern Highlands regions. We'd recommend contacting a local licensed builder in your area.",
      "regional_callouts": {
        "allowed": true,
        "min_job_value": "$15,000",
      },
    },
    "credentials": {
      "registration_number": "ABN 12 345 678 901",
      "license_holder_name": "Coastal Renovation Group Pty Ltd",
      "license_number": "BLD-NSW-456789",
      "insurance": "Public Liability Insurance – $20M cover",
      "warranty_terms": "5-year workmanship warranty on bathroom and kitchen renovations; manufacturer warranty on all fixtures and materials.",
    },
    "legal_and_compliance": {
      "privacy_policy_url": "https://coastalrenovationgroup.com.au/privacy",
      "state_regulations": "Under NSW Home Building Act, contracts over $5,000 include a 5-business-day cooling-off period.",
    },
    "competitive_strategy": {
      "competitors_often_mentioned": [
        "Budget Reno Co",
        "Quick Fix Builders",
      ],
      "design_visualisation": {
        "offered": true,
        "stage_name": "3D Design",
        "short_description": "Photo-realistic 3D visualisation before construction starts",
      },
      "core_differentiator": "Fixed-Price Guarantee, photo-realistic 3D design before build, in-house trades, single contract",
      "unique_selling_points": [
        "Fixed-Price Guarantee — contract price is final",
        "3D visualisation before construction starts",
        "In-house kitchen, bathroom and build teams",
        "150+ completed projects, 10+ years, 5-star Google reviews",
        "One contract, one project manager",
      ],
      "process_overview": "Free Consultation → 3D Design & Quote → Construction → Handover",
    },
    "pricing_policy": {
      "quote_rules": {
        "free_quotes_standard": true,
        "fixed_price_guarantee_enabled": true,
        "fixed_price_guarantee_lock_description": "Your contract price is locked in after the 3D design stage",
      },
      "job_requirements": {
        "minimum_job_value": "$15,000",
        "deposit_required": "10% upon contract signing",
      },
      "payment_and_finance": {
        "tax_status": "GST inclusive (10%)",
        "payment_methods": [
          "Bank Transfer",
          "Credit Card (Visa/Mastercard)",
          "Cash on completion milestones",
        ],
        "financing_options": {
          "offered": true,
          "provider_name": "Brighte Finance",
        },
      },
      "ballpark_pricing": {
        "enforce_no_exact_prices": true,
        "products": [
          {
            "product_name": "Bathroom renovation (premium)",
            "pricing": "$25,000 – $45,000",
          },
          {
            "product_name": "Kitchen renovation (premium)",
            "pricing": "$35,000 – $75,000",
          },
          {
            "product_name": "Extension / addition",
            "pricing": "$80,000 – $250,000+",
          },
          {
            "product_name": "Free consultation visit",
            "pricing": "FREE – no obligation",
          },
          {
            "product_name": "Emergency make-safe / water damage",
            "pricing": "$350 – $800 call-out",
          },
        ],
      },
    },
    "services_offered": {
      "kitchen_renovation": "true",
      "bathroom_renovation": "true",
      "whole_home_renovation": "true",
      "extension_addition": "true",
      "granny_flat_secondary_dwelling": "true",
      "partial_home_renovation": "true",
      "laundry_renovation": "true",
      "flooring_installation": "sometimes",
      "wall_tiling": "sometimes",
      "painting_decorating": "sometimes",
      "insurance_restoration_repair": "true",
      "commercial_fit_out": "false",
      "design_consult_only": "false",
      "structural_engineering_only": "false",
      "new_build_construction_only": "false",
      "asbestos_removal_standalone": "false",
      "pool_spa_installation": "false",
      "landscaping_only": "false",
      "architectural_drawings_only": "false",
    },
    "out_of_scope_services": [
      "structural_engineering_only",
      "new_build_construction_only",
      "asbestos_removal_standalone",
      "pool_spa_installation",
      "landscaping_only",
      "architectural_drawings_only",
      "design_consult_only",
    ],
    "emergency_safety_actions": {
      "active_water_flooding": "Turn off the water at the mains if you can do so safely, move valuables off the floor, and avoid standing in pooled water.",
      "structural_collapse_or_imminent": "Stay out of the affected room and do not enter areas where the ceiling or walls may be unstable.",
      "ceiling_collapse_or_imminent": "Stay clear of the affected area and do not walk under damaged ceiling sections.",
      "storm_electrical_involvement": "Do not touch wet switches or appliances; turn off power at the switchboard only if it is safe to do so.",
      "gas_or_chemical_smell_after_damage": "Leave the area, avoid switches and flames, and wait for professional assessment.",
      "default": "Move to a safe area away from the hazard and avoid further work until our team assesses the situation.",
    },
    "handoff_settings": {
      "handoff_triggers": [
        "customer_requests_human",
        "aggressive_behavior",
        "complex_insurance_claim",
      ],
      "handoff_messages": {
        "general_escalation": "I'll connect you with Sarah, our Office Manager, who can help from here. She'll be in touch shortly during business hours.",
        "emergency_escalation": "I'm routing this to Sarah, our Office Manager, now — she'll call you within 15 minutes.",
        "generic_failure": "We're having a brief technical issue. Please try again in a moment or call us on +61 2 4222 0001.",
      },
      "human_request_handoff_threshold": 3,
      "emergency_sla_text": "15 minutes",
      "roles": [
        {
          "type": "general",
          "role": "Office Manager",
          "name": "Sarah",
          "phone": "+61 2 4222 0001",
          "email": "hello@coastalrenovationgroup.com.au",
        },
      ],
    },
    "objection_handling": [
      {
        "objection": "Price is too high / I found it cheaper",
        "response": "I understand — renovation is a big investment. Our Fixed-Price Guarantee means the contract price is final, and you see a photo-realistic 3D design before we start. We use in-house trades and stand behind a 5-year workmanship warranty on kitchen and bathroom work.",
      },
      {
        "objection": "I need to think about it",
        "response": "Absolutely — there's no pressure. The free consultation is obligation-free, and you'll leave with a clear scope and ballpark range. Would a morning or afternoon visit suit better?",
      },
      {
        "objection": "Can you match a competitor quote?",
        "response": "We focus on delivering a fixed price with quality in-house teams rather than racing to the bottom. Happy to walk you through what's included in our quote so you can compare fairly.",
      },
    ],
  },
  "qualification_logic": {
    "lite_qualification_enabled": false,
    "booking_gate_keys": [
      "client_name",
      "contact_phone",
      "contact_email",
      "suburb",
      "renovation_scope",
    ],
    "strict_qualification_gate": true,
    "skip_qualification_if_known_user": false,
    "interviewer_policy": {
      "repetition_soft_ask_threshold": 3,
      "refusal_escalation_threshold": 2,
    },
    "unqualified_action": "decline_politely",
    "questions": [
      {
        "question": "Can I get your name please?",
        "key": "client_name",
        "internal_goal": "Personalise the booking",
        "turn_on": true,
        "optional": false,
      },
      {
        "question": "What's the best phone number to reach you on?",
        "key": "contact_phone",
        "internal_goal": "Collect contact details",
        "turn_on": true,
        "optional": false,
      },
      {
        "question": "What's the best email to send your confirmation to?",
        "key": "contact_email",
        "internal_goal": "Collect contact details",
        "turn_on": true,
        "optional": true,
      },
      {
        "question": "Which suburb is the property in?",
        "key": "suburb",
        "internal_goal": "Service area check and scheduling",
        "turn_on": true,
        "optional": false,
      },
      {
        "question": "Which areas are you looking to renovate (e.g., kitchen, bathroom, whole home, extension)?",
        "key": "project_type",
        "internal_goal": "Match appointment type and scope for quoting",
        "turn_on": true,
        "optional": false,
      },
      {
        "question": "Are you thinking more of a full remodel — new layout, fixtures, cabinetry — or more of an update or refresh, like replacing countertops or fixtures?",
        "key": "renovation_scope",
        "internal_goal": "Estimate scope depth and match appointment type for quoting",
        "turn_on": true,
        "optional": true,
      },
      {
        "question": "What's the full address of the property?",
        "key": "property_address",
        "internal_goal": "Service area check and logistics planning",
        "turn_on": true,
        "optional": false,
      },
      {
        "question": "What's your ideal timeline for getting this project started — are you hoping to begin in the next few weeks, a few months, or is it still in the planning phase?",
        "key": "project_timeline",
        "internal_goal": "Determine lead urgency and prioritize calendar booking (immediate dispatch for hot leads vs nurture sequence for planning phase)",
        "turn_on": true,
        "optional": true,
      },
      {
        "question": "Is this your primary residence, or is it a rental or investment property?",
        "key": "property_status",
        "internal_goal": "Identify the emotional driver and pricing threshold (owner-occupiers buy premium finishes/luxury; investors focus strictly on ROI and durable low-cost materials)",
        "turn_on": true,
        "optional": true,
      },
      {
        "question": "Besides yourself, will anyone else be involved in the decision-making — like a spouse or partner? Just so we make sure the right people are included in the consultation.",
        "key": "decision_makers",
        "internal_goal": "Guarantee 100% attendance of all stakeholders at the live consultation to prevent the \"I need to talk to my wife\" sales objection.",
        "turn_on": true,
        "optional": true,
      },
      {
        "question": "Just so I can help us set the right expectations — do you have a rough budget in mind for this project? It doesn't have to be exact, even a ballpark helps.",
        "key": "budget_range",
        "internal_goal": "Filter out tire-kickers who don't match premium Coastal Renovation Group pricing standards before booking a free site visit.",
        "turn_on": true,
        "optional": true,
      },
      {
        "question": "Have you spoken to any other contractors yet, or are you just getting started with exploring your options?",
        "key": "competitor_status",
        "internal_goal": "Define the sales strategy for the estimator (are we fighting a price war with competitors or setting the anchor price as the first option).",
        "turn_on": true,
        "optional": true,
      },
      {
        "question": "Could you send us some photos? It helps our designer come ready with ideas tailored specifically to your space.",
        "key": "current_space_photos",
        "internal_goal": "Visual pre-qualification of structural state (e.g., detecting obvious water damage/asbestos risks) and feeding the designer context to prepare the 3D render hook.",
        "turn_on": true,
        "optional": true,
      },
      {
        "question": "What day and time works best for our designer to come out for your free consultation? We have slots available this week.",
        "key": "appointment_slot",
        "internal_goal": "Lock the exact day, date, and time into the contractor's calendar to complete the booking protocol.",
        "turn_on": true,
        "optional": false,
      },
    ],
    "disqualification_rules": {
      "blocked_property_types": [
        "commercial",
      ],
      "asbestos_policy": "refer_to_licensed_asbestos_specialist",
      "tenant_policy": "require_owner_consent",
      "max_bridge_turns": 3,
      "rejection_messages": {
        "out_of_area": "We currently service the Illawarra, South Coast, and Southern Highlands — we'd recommend a local licensed builder in your area.",
        "service_not_offered": "While we'd love to help, our in-house teams don't currently handle that specific type of work.",
        "property_type": "We specialise in residential renovations and aren't the right fit for this property type.",
        "tenant_no_authority": "As a tenant, we'll need the property owner or strata manager to authorise the work before we can proceed.",
        "asbestos_referral": "If asbestos may be present, we'll refer you to a licensed asbestos specialist for assessment first.",
        "budget_too_low": "Thanks for sharing your budget — our projects typically start at a higher minimum. We may not be the right fit for this scope, but we appreciate you reaching out.",
        "default_polite_no": "Thank you for reaching out — it doesn't look like we're the right fit for this project at this time.",
      },
    },
  },
  "scheduling": {
    "meeting_type": "Free Consultation",
    "default_duration_minutes": 60,
    "channel_schedules": {
      "site_visit": {
        "monday": {
          "start": "07:00",
          "end": "15:00",
        },
        "tuesday": {
          "start": "07:00",
          "end": "15:00",
        },
        "wednesday": {
          "start": "07:00",
          "end": "15:00",
        },
        "thursday": {
          "start": "07:00",
          "end": "15:00",
        },
        "friday": null,
        "saturday": {
          "start": "08:00",
          "end": "12:00",
        },
        "sunday": null,
      },
      "phone_consult": {
        "monday": {
          "start": "08:30",
          "end": "17:00",
        },
        "tuesday": {
          "start": "08:30",
          "end": "17:00",
        },
        "wednesday": {
          "start": "08:30",
          "end": "17:00",
        },
        "thursday": {
          "start": "08:30",
          "end": "17:00",
        },
        "friday": {
          "start": "08:30",
          "end": "17:00",
        },
        "saturday": {
          "start": "09:00",
          "end": "13:00",
        },
        "sunday": null,
        "out_of_hours_message": "Our office is currently closed. For emergencies, call our on-call project manager — we'll respond within 15 minutes.",
      },
    },
    "operating_hours": {
      "public_holidays": [
        {
          "date": "2026-12-25",
          "name": "Christmas Day",
          "surcharge_fee": "$100",
        },
        {
          "date": "2026-12-26",
          "name": "Boxing Day",
          "surcharge_fee": "$100",
        },
        {
          "date": "2026-01-01",
          "name": "New Year's Day",
          "surcharge_fee": "$100",
        },
      ],
    },
    "logistics": {
      "travel_buffer_minutes": 30,
      "appointment_time_format": "arrival_window",
      "window_size_hours": 2,
      "max_appointments_per_day": 5,
    },
    "booking_rules": {
      "visit_min_lead_time_hours": 24,
      "call_min_lead_time_hours": 4,
      "max_lead_time_days": 30,
    },
    "booking_channel": {
      "visit_type": "free_quote_inspection",
      "call_type": "phone_video_consult",
    },
    "media_requirements": {
      "default_photo_prompts": [
        "Wide shot showing the full room or affected area",
        "Close-up of the damage or detail you want addressed",
      ],
    },
    "team_calendars": [
      {
        "platform": "ghl",
        "calendar_id": "mock-renovation-calendar-001",
        "label": "Consultant",
        "handles_appointment_types": [
          "free_quote_inspection",
        ],
      },
      {
        "platform": "ghl",
        "calendar_id": "mock-renovation-office-001",
        "label": "Office",
        "handles_appointment_types": [
          "phone_video_consult",
        ],
      },
    ],
    "appointment_types": {
      "free_quote_inspection": {
        "is_offered": true,
        "label": "Free Inspection",
        "duration_minutes": 60,
        "is_free": true,
        "price": null,
        "attendee": "project_consultant",
        "prerequisites": [
          "address",
          "problem description",
        ],
      },
      "phone_video_consult": {
        "is_offered": true,
        "label": "Phone / Video Consult",
        "duration_minutes": 30,
        "is_free": true,
        "price": null,
        "attendee": "office_staff",
      },
    },
  },
  "tone_of_voice": {
    "bot_name": "Alex",
    "disclose_ai_status": true,
    "style": "professional_friendly",
    "use_emojis": false,
    "use_aussie_slang": false,
    "forbidden_phrases": [
      "100% covered",
    ],
    "allowed_brand_phrases": [
      "Fixed-Price Guarantee",
    ],
    "preferred_phrases": [
      "We bring your renovation vision to life",
      "Quality craftsmanship you can trust",
    ],
    "replacement_phrases": {
      "reach_out_scrub": [
        "Happy to keep going right here — what's the next step?",
      ],
      "scheduling_cta": [
        "I can get that scheduled — which day suits you?",
      ],
    },
    "emoji_policy": {
      "max_per_session": 2,
      "max_per_message": 1,
    },
    "tagline": "Renovations you can plan with confidence",
  },
};

export function cloneRenovationDefaults() {
  return JSON.parse(JSON.stringify(RENOVATION_BOT_SETTINGS_DEFAULTS));
}
